import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { corporateTheme } from '../themes/corporate';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${corporateTheme.backgroundGradient};
  padding: 120px 2rem 4rem;
  font-family: ${corporateTheme.fontFamily};
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 700;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 2rem;
  text-align: center;
`;

const LastUpdated = styled.p`
  text-align: center;
  color: ${corporateTheme.textSecondary};
  margin-bottom: 3rem;
  font-style: italic;
`;

const Content = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  border-radius: 20px;
  padding: 3rem;
  box-shadow: ${corporateTheme.cardShadow};
  border: 1px solid ${corporateTheme.cardBorder};
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: ${corporateTheme.primary};
  margin-bottom: 1rem;
  font-weight: 600;
`;

const Text = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.7;
  margin-bottom: 1rem;
`;

const List = styled.ul`
  color: ${corporateTheme.textSecondary};
  line-height: 1.7;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
`;

const PrivacyPolicy = () => {
  return (
    <PageContainer>
      <Container>
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Privacy Policy
        </Title>
        
        <LastUpdated>Last updated: December 2024</LastUpdated>
        
        <Content
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Section>
            <Text>
              GrowTech Ltd ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our affiliate 
              marketing platform and services.
            </Text>
          </Section>

          <Section>
            <SectionTitle>1. Information We Collect</SectionTitle>
            <Text>
              We collect information you provide directly to us, such as when you create an account, 
              fill out a form, or communicate with us. This may include:
            </Text>
            <List>
              <li>Name, email address, and contact information</li>
              <li>Account credentials and profile information</li>
              <li>Payment and billing information</li>
              <li>Business information for affiliate partnerships</li>
              <li>Communications and support interactions</li>
              <li>Website analytics and usage data</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>2. How We Use Your Information</SectionTitle>
            <Text>We use the information we collect to:</Text>
            <List>
              <li>Provide, maintain, and improve our affiliate marketing services</li>
              <li>Process transactions and calculate commissions</li>
              <li>Send technical notices and support messages</li>
              <li>Communicate about new features, offers, and important updates</li>
              <li>Monitor and analyze trends and usage patterns</li>
              <li>Prevent fraud and ensure platform security</li>
              <li>Comply with legal obligations</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>3. Information Sharing and Disclosure</SectionTitle>
            <Text>
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, 
              except as described in this policy. We may share information with:
            </Text>
            <List>
              <li>Service providers who assist in our operations (hosting, payment processing, analytics)</li>
              <li>Business partners for legitimate business purposes (with your consent)</li>
              <li>Law enforcement or government agencies when required by law</li>
              <li>Professional advisors (lawyers, accountants) under confidentiality agreements</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>4. Data Security</SectionTitle>
            <Text>
              We implement appropriate security measures to protect your personal information against unauthorized access, 
              alteration, disclosure, or destruction. These measures include encryption, secure servers, and regular 
              security audits. However, no method of transmission over the internet is 100% secure.
            </Text>
          </Section>

          <Section>
            <SectionTitle>5. Your Rights and Choices</SectionTitle>
            <Text>You have the right to:</Text>
            <List>
              <li>Access and update your personal information</li>
              <li>Request deletion of your account and data</li>
              <li>Opt-out of marketing communications</li>
              <li>Request a copy of your data</li>
              <li>Withdraw consent where processing is based on consent</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>6. Data Retention</SectionTitle>
            <Text>
              We retain your personal information for as long as necessary to provide our services and comply with legal 
              obligations. Account information is typically retained for the duration of your account plus any required 
              legal retention periods.
            </Text>
          </Section>

          <Section>
            <SectionTitle>7. International Data Transfers</SectionTitle>
            <Text>
              As we operate in Eastern Asian markets, your information may be transferred to and processed in countries 
              other than your own. We ensure appropriate safeguards are in place for such transfers.
            </Text>
          </Section>

          <Section>
            <SectionTitle>8. Changes to This Policy</SectionTitle>
            <Text>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
              Privacy Policy on this page and updating the "Last updated" date.
            </Text>
          </Section>

          <Section>
            <SectionTitle>9. Contact Us</SectionTitle>
            <Text>
              If you have any questions about this Privacy Policy, please contact us at:
              <br /><br />
              <strong>GrowTech Ltd</strong><br />
              Email: info@growtech.cc<br />
              Address: Hong Kong
            </Text>
          </Section>
        </Content>
      </Container>
    </PageContainer>
  );
};

export default PrivacyPolicy; 